import { dialog, GridLayout, IDialog, IGridLayoutParameters, IInputParameters, Input } from '@Common/Controls';
import constants from '@Common/Core/Constant';

import Maybe from '@Common/Core/Maybe';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { IntervalPickerType, TimeWindowDateVisibility, TypeKind } from '@Common/Enums';

import localizationHelper from '@Common/Helpers/LocalizationHelper';
import { dateTimeInterval } from '@Common/Helpers/TimeHelper';
import { DateTimeInterval, TextStyle } from '@InstanceFactory';
import { enumerable } from '@Common/Core/CoreDecorator';

export class TimeSelectionParams {
    public showTime = false;
    public showNoSelection = false;
    public noSelectionText = strings.noFilter;
    public timeInterval = dateTimeInterval(constants.minDate, constants.maxDate);
    public dateVisibility: TimeWindowDateVisibility = TimeWindowDateVisibility.both;
    public layoutType: IntervalPickerType = IntervalPickerType.picker;
    public style: TextStyle;

    constructor(params?: Partial<TimeSelectionParams>) {
        _.assign(this, params);
    }
}

abstract class IntervalSelectionControlBase {
    protected root: GridLayout;
    protected events = new MulticastDelegateBag();
    public onSelectionChanged = this.events.create<ICallback<DateTimeInterval>>();

    constructor(protected params: TimeSelectionParams) {}

    public getControl(): JQuery<HTMLElement> {
        return this.root.getControl();
    }

    public adjust(w: number, h: number): void {
        this.root.adjust(w, h);
    }

    public abstract setValue(interval: DateTimeInterval): void;
}

class DualPickerControl extends IntervalSelectionControlBase {
    private fromPicker;
    private toPicker;

    constructor(params: TimeSelectionParams) {
        super(params);
        this.fromPicker = $('<div/>');
        this.toPicker = $('<div/>');

        const canChangeFromDate = this.params.dateVisibility === TimeWindowDateVisibility.toOnly;
        const fromConfig = canChangeFromDate ? this.disabledPickerConfiguration : this.pickerConfiguration;
        this.fromPicker[this.pickerPropertyName](fromConfig);

        const canChangeToDate = this.params.dateVisibility === TimeWindowDateVisibility.fromOnly;
        const toConfig = canChangeToDate ? this.disabledPickerConfiguration : this.pickerConfiguration;
        this.toPicker[this.pickerPropertyName](toConfig);

        this.root = new GridLayout({
            className: 'time-selection-dialog',
            columns: [{ style: { padding: '5px' } }, { style: { padding: '5px' } }],
            rows: [{ style: { height: 'auto' } }, { style: { height: 'auto' } }, { style: { height: '*' } }],
            cells: [
                { rowIndex: 0, columnIndex: 0, content: strings.from },
                { rowIndex: 0, columnIndex: 1, content: strings.to },
                { rowIndex: 1, columnIndex: 0, content: this.fromPicker, className: 'date-picker-container' },
                { rowIndex: 1, columnIndex: 1, content: this.toPicker, className: 'date-picker-container' }
            ]
        });
    }

    @enumerable
    private get pickerPropertyName(): string {
        return this.params.showTime ? 'datetimepicker' : 'datepicker';
    }

    @enumerable
    private get pickerConfiguration(): any {
        return {
            changeMonth: true,
            changeYear: true,
            yearRange: 'c-5:c+5',
            yearSuffix: '',
            showButtonPanel: true,
            currentText: this.params.showTime ? strings.timePicker.currentText : strings.timePicker.currentDayText,
            onChangeMonthYear: (year, month, inst) => {
                if (inst.currentYear !== year || inst.currentMonth !== month - 1) {
                    const curDate = inst.input.datepicker('getDate');
                    curDate.setAbsoluteYear(year, month - 1);
                    inst.input.datepicker('setDate', curDate);
                    this.processDateChanging();
                }
            },
            onSelect: () => this.processDateChanging()
        };
    }

    @enumerable
    private get disabledPickerConfiguration(): any {
        return _.extend(this.pickerConfiguration, {
            disabled: true,
            changeMonth: false,
            changeYear: false,
            showHour: false,
            showMinute: false,
            showSecond: false,
            showButtonPanel: false
        });
    }

    private processDateChanging(): void {
        const interval = DateTimeInterval.create({
            from: this.fromPicker.datepicker('getDate'),
            to: this.toPicker.datepicker('getDate')
        });
        this.onSelectionChanged(interval);
    }

    public setValue(interval: DateTimeInterval): void {
        interval.from && this.fromPicker.datepicker('setDate', interval.from);
        interval.to && this.toPicker.datepicker('setDate', interval.to);
    }
}

class DualInputControl extends IntervalSelectionControlBase {
    private fromDate: Input;
    private toDate: Input;
    private skipCallback = false;
    private inputFormat: string;

    @enumerable
    private get dateInputParams(): IInputParameters {
        return {
            type: TypeKind.dateTime,
            showTime: this.params.showTime,
            width: 200
        };
    }

    constructor(params: TimeSelectionParams) {
        super(params);
        this.inputFormat = localizationHelper.getInputDateTimeFormat(this.params.showTime);

        this.toDate = new Input(this.dateInputParams);
        this.toDate.setState(this.params.dateVisibility !== TimeWindowDateVisibility.fromOnly);
        this.fromDate = new Input(this.dateInputParams);
        this.fromDate.setState(this.params.dateVisibility !== TimeWindowDateVisibility.toOnly);

        this.params.style && this.applyCustomStyles(this.params.style, this.fromDate, this.toDate);
        _.each([this.toDate, this.fromDate], c => c.onChange(() => this.processDateChange()));

        this.root = new GridLayout(this.getLayoutParams());
    }

    private applyCustomStyles(style: any, ...crls: Input[]): void {
        _.each(crls, c => {
            c.getControl().find('.ui-datepicker-trigger').css('background', style.background);
            c.getControl().addClass('hide-arrow');
        });
    }

    private processDateChange(): void {
        if (this.skipCallback) {
            return;
        }
        const from = this.fromDate.getValue();
        const to = this.toDate.getValue();
        if (from && to) {
            this.onSelectionChanged({ from, to });
        } else {
            this.onSelectionChanged(null);
        }
    }

    public setValue(interval: DateTimeInterval): void {
        this.skipCallback = true;
        this.toDate.setValue(Maybe(interval).get('to').value);
        this.fromDate.setValue(Maybe(interval).get('from').value);
        this.skipCallback = false;
    }

    private getLayoutParams(): IGridLayoutParameters {
        const columns = [
            { style: { width: '*', paddingRight: '5px' } },
            {
                style: {
                    width: '*',
                    paddingRight: '5px'
                }
            }
        ];
        const rows = [{ style: { height: 'auto' } }, { style: { height: 'auto' } }, { style: { height: '*' } }];

        return {
            columns: columns,
            rows: rows,
            cells: [
                { columnIndex: 0, rowIndex: 0, content: strings.wizards.time.from },
                { columnIndex: 0, rowIndex: 1, content: this.fromDate },
                { columnIndex: 1, rowIndex: 0, content: strings.wizards.time.to },
                { columnIndex: 1, rowIndex: 1, content: this.toDate }
            ]
        };
    }
}

export class TimeSelectionDialog {
    private readonly selectionControl: IntervalSelectionControlBase;
    private readonly events = new MulticastDelegateBag();
    public onOk = this.events.create<ICallback<DateTimeInterval>>();
    public onCancel = this.events.create<IHandler>();
    public onClearSelection = this.events.create<IHandler>();
    private interval: DateTimeInterval;
    private grid: GridLayout;
    private dialog: IDialog;
    private errorMsg: JQuery<HTMLElement>;

    constructor(private params: TimeSelectionParams) {
        this.interval = _.cloneDeep(params.timeInterval);
        this.selectionControl =
            this.params.layoutType === IntervalPickerType.input ? new DualInputControl(this.params) : new DualPickerControl(this.params);
        this.selectionControl.onSelectionChanged(newInterval => {
            if (this.validate(newInterval)) {
                this.interval = newInterval;
            }
        });
    }

    public show(): void {
        const noSelectionButtonConfig = {
            ready: () => {
                this.onClearSelection();
                this.dialog.remove();
            },
            text: this.params.noSelectionText
        };
        this.dialog = dialog({
            title: strings.selectTime,
            buttons: this.params.showNoSelection ? [noSelectionButtonConfig] : []
        });
        this.errorMsg = $('<div width="100%" height= "100%">');
        this.grid = new GridLayout({
            columns: [{}],
            rows: [{ style: { height: 'auto' } }, { style: { height: '28px' } }],
            cells: [
                { rowIndex: 0, columnIndex: 0, content: this.selectionControl },
                { rowIndex: 1, columnIndex: 0, content: this.errorMsg }
            ]
        });

        this.dialog.wrap(this.grid);
        let isOk = false;
        this.dialog.ok(() => {
            isOk = true;
            this.onOk(this.interval);
        });
        this.dialog.cancel(() => this.onCancel());
        this.dialog.onClose(() => {
            if (!isOk) {
                this.onCancel();
            }
            this.events.destroy();
        });
        this.selectionControl.setValue(this.params.timeInterval);
    }

    private validate(interval: DateTimeInterval): boolean {
        const state = interval && interval.from <= interval.to;
        this.dialog.setOkButtonState(state);
        if (!state) {
            this.errorMsg.addClass('error').text(strings.validationMessages.invalidInterval);
        } else {
            this.errorMsg.removeClass('error').text('');
        }
        return state;
    }

    public getValue(): DateTimeInterval {
        return this.interval;
    }
}
